// =============================================================================
// EDGE FUNCTIONS CATALOG
//
// Hand-maintained reference list of every Supabase edge function across the
// App project (apps/unravel-app-v2) and the Platform project (this repo).
//
// Surfaced at /admin/edge-functions so operators can answer:
//   - "What does function X actually do?"
//   - "Which UI / feature calls it?"
//   - "Is it a user action, a cron, a webhook, or a DB trigger?"
//
// **KEEP IN SYNC**: when you add/rename/remove an edge function, update
// this file. The categories + triggers are free-text on purpose — adding a
// new one is fine, but try the existing set first so the filter dropdowns
// stay coherent.
// =============================================================================

export type EdgeFnProject  = 'app' | 'platform';

/** Top-level grouping for the filter dropdown. Pick the closest match;
 *  prefer adding a new bucket only if a category would have ≥3 members. */
export type EdgeFnCategory =
  | 'AI / Generation'
  | 'AI / Assistance'
  | 'AI / Vision & Voice'
  | 'Auth & Identity'
  | 'Trip Guardian (flights)'
  | 'Live Trip & Companion'
  | 'Social Publishing'
  | 'Stripe / B2B billing'
  | 'Stripe / B2C billing'
  | 'Email & Push'
  | 'Cross-project bridge'
  | 'Tenant & User admin'
  | 'Lookup / Proxy'
  | 'Cron / Cleanup'
  | 'Public / Anonymous'
  | 'Infra & Ops'
  | 'Other';

/** What causes this function to fire. */
export type EdgeFnTrigger =
  | 'user-action'   // direct UI button or auto-firing client effect
  | 'cron'          // pg_cron scheduled
  | 'webhook'       // external service POSTs in (Stripe, Cloudflare, etc.)
  | 'db-trigger'    // Postgres trigger via pg_net (async fire-and-forget)
  | 'public'        // unauthenticated / public endpoint
  | 'internal';     // invoked by another edge function

export interface EdgeFnEntry {
  /** Function directory name under supabase/functions/. */
  name:        string;
  project:     EdgeFnProject;
  category:    EdgeFnCategory;
  trigger:     EdgeFnTrigger;
  /** One sentence: what it does. */
  description: string;
  /** Where / when it gets called. Be specific — file path or feature name. */
  caller:      string;
}

// ─────────────────────────────────────────────────────────────────────────────
// APP project — apps/unravel-app-v2/supabase/functions/
// ─────────────────────────────────────────────────────────────────────────────

const APP: EdgeFnEntry[] = [
  // ── AI / Generation ─────────────────────────────────────────────────────
  { name: 'generate-itinerary', project: 'app', category: 'AI / Generation', trigger: 'user-action',
    description: 'Builds the main multi-day trip itinerary. Picks LLM (OpenAI/Anthropic) from Platform AI Settings, runs Main+Supplements in parallel.',
    caller: 'AiTripBuilder step 7 "Generate" + ResultsScreen rebuild' },
  { name: 'generate-messaging-itinerary', project: 'app', category: 'AI / Generation', trigger: 'internal',
    description: 'Service-role clone of generate-itinerary for the WhatsApp "plan a trip" build: takes an explicit user_id (no JWT), runs synchronously, and persists the trip itself (trips + trip_itineraries + budget), returning trip_id.',
    caller: 'messaging-inbound (background) after a WhatsApp build confirms' },
  { name: 'generate-hotel-itinerary', project: 'app', category: 'AI / Generation', trigger: 'user-action',
    description: 'Experimental hotel-anchored itinerary variant. Runs per-city in parallel using the chosen hotel as anchor.',
    caller: 'AiTripBuilder dual-build path → Near Hotel tab on Results' },
  { name: 'generate-hotel-picks', project: 'app', category: 'AI / Generation', trigger: 'user-action',
    description: 'Per-city hotel recommendations on demand; backfills trips that lack recommended_hotels[].',
    caller: 'Stay tab → "Suggest hotels" button' },
  { name: 'generate-insider', project: 'app', category: 'AI / Generation', trigger: 'user-action',
    description: 'On-demand "supplements" (food, neighbourhoods, etiquette, channels) for an already-built itinerary.',
    caller: 'ResultsScreen prefetch + Insider tab refresh' },
  { name: 'generate-transit-options', project: 'app', category: 'AI / Generation', trigger: 'user-action',
    description: 'Per-leg modal comparison for intercity moves (rail/bus/flight/drive).',
    caller: 'Transit tab on ResultsScreen' },
  { name: 'generate-trip-requirements', project: 'app', category: 'AI / Generation', trigger: 'user-action',
    description: 'Expanded pre-trip checklist (visa, vaccines, plug types). Personalises against passport + destinations.',
    caller: 'Requirements tab refresh' },
  { name: 'generate-daily-journal', project: 'app', category: 'AI / Generation', trigger: 'user-action',
    description: 'AI-generated journal entry for one day, drafted from notes + photos.',
    caller: 'Journal tab → "Generate today\'s journal" + Live Dashboard day-wrapped CTA' },
  { name: 'generate-trip-journal', project: 'app', category: 'AI / Generation', trigger: 'user-action',
    description: 'AI-generated whole-trip journal narrative covering the entire trip cohesively.',
    caller: 'JournalHistory → TripJournalGenerateModal' },
  { name: 'generate-trip-reel', project: 'app', category: 'AI / Generation', trigger: 'user-action',
    description: 'Kicks off a Creatomate 1080×1920 Ken-Burns slideshow render from trip journal photos + title.',
    caller: 'JournalHistory → TripReelGenerateModal "Generate Reel"' },
  { name: 'parse-ai-trip', project: 'app', category: 'AI / Generation', trigger: 'user-action',
    description: 'Parses a free-form trip description ("paris/rome 10 days") into structured AiTripBuilder fields.',
    caller: 'Trip Builder Step 1 quick-paste input' },
  { name: 'enrich-activity', project: 'app', category: 'AI / Generation', trigger: 'user-action',
    description: 'On-demand AI enrichment for a single activity card (details, costs, tips).',
    caller: 'Activity card expand on ResultsScreen / NewLookDayCard' },
  { name: 'schedule-itinerary', project: 'app', category: 'AI / Generation', trigger: 'user-action',
    description: 'Blitz-style scheduler that adds per-activity clock times to an imported itinerary that lacks them.',
    caller: 'Import flow auto-schedule modal' },

  // ── AI / Assistance ─────────────────────────────────────────────────────
  { name: 'clarify-trip-plan', project: 'app', category: 'AI / Assistance', trigger: 'user-action',
    description: 'Conversational trip-plan refinement — interprets ambiguous trip requests, redistributes days, resolves conflicts.',
    caller: 'Trip Builder step 1 chat + TripConfirmModal refinement' },
  { name: 'trip-chat', project: 'app', category: 'AI / Assistance', trigger: 'user-action',
    description: '"Blitz" general chat — modifies activities, answers questions about a trip. Honours Platform AI Settings.',
    caller: 'Blitz bubble (ResultsScreen, Day-by-Day, Near Hotel V2)' },
  { name: 'day-blitz', project: 'app', category: 'AI / Assistance', trigger: 'user-action',
    description: 'Async full-day rebuild. Inserts a blitz_jobs row, returns job_id; client subscribes via Realtime.',
    caller: 'Day Blitz panel — "Rebuild this day" + scope-picker' },
  { name: 'check-budget-fit', project: 'app', category: 'AI / Assistance', trigger: 'user-action',
    description: 'Quick budget-vs-destination sanity check before user finishes wizard. Returns tight/comfortable/generous verdict.',
    caller: 'AiTripBuilder budget step (debounced)' },
  { name: 'duration-check', project: 'app', category: 'AI / Assistance', trigger: 'user-action',
    description: 'Trip-length sanity check given destinations + tentative days + flight origin.',
    caller: 'AiTripBuilder where-when step' },
  { name: 'cultural-guide', project: 'app', category: 'AI / Assistance', trigger: 'user-action',
    description: 'Generates the Culture card content (greetings, etiquette, customs) for a destination.',
    caller: 'Overview tab → Culture card' },
  { name: 'flight-guide', project: 'app', category: 'AI / Assistance', trigger: 'internal',
    description: 'Trip Guardian agentic layer — composes arrival/delay guidance messages for a specific flight.',
    caller: 'flight-status-poll fans out to this on disruption' },
  { name: 'packing-list', project: 'app', category: 'AI / Assistance', trigger: 'user-action',
    description: 'AI packing-list generator tuned to destination weather + activities.',
    caller: 'Essentials tab → Packing card' },
  { name: 'secret-menu', project: 'app', category: 'AI / Assistance', trigger: 'user-action',
    description: 'Restaurant "secret menu" / off-menu picks per destination.',
    caller: 'Food tab on ResultsScreen' },
  { name: 'smart-companion', project: 'app', category: 'AI / Assistance', trigger: 'user-action',
    description: 'Server-side companion brain — composes nearby-tip toasts based on coords + time-of-day.',
    caller: 'CompanionMount in AppLayout (every in-trip route)' },
  { name: 'local-insights', project: 'app', category: 'AI / Assistance', trigger: 'user-action',
    description: 'Cached local insights (events, weather quirks, "what\'s open today") with shared cache table.',
    caller: 'useLocalInsights hook on Live Dashboard + Itinerary' },
  { name: 'copilot-nudge', project: 'app', category: 'AI / Assistance', trigger: 'user-action',
    description: 'AI-generated nudge / hint text shown in the trip-builder copilot UI.',
    caller: 'Trip Builder copilot bubble' },
  { name: 'summarize-itinerary-preferences', project: 'app', category: 'AI / Assistance', trigger: 'user-action',
    description: 'Two-section private-chat "Summary" — viewer-only modal explaining what the AI inferred.',
    caller: 'Build modal → Summary button' },
  { name: 'app-assistant', project: 'app', category: 'AI / Assistance', trigger: 'user-action',
    description: 'Generic app-side help assistant (in-app Q&A).',
    caller: 'Settings → Help / AskMe widget' },
  { name: 'register-agent', project: 'app', category: 'AI / Assistance', trigger: 'user-action',
    description: 'Registers the AI agent context (B2B/B2C offerings) for ask-me / app-assistant.',
    caller: 'AskMe widget bootstrap' },

  // ── AI / Vision & Voice ─────────────────────────────────────────────────
  { name: 'extract-itinerary', project: 'app', category: 'AI / Vision & Voice', trigger: 'user-action',
    description: 'Parses an existing itinerary (URL/text/Word) via Claude into the GeneratedItinerary schema.',
    caller: 'Import-trip flow (paste URL, paste text, upload .docx)' },
  { name: 'extract-receipt', project: 'app', category: 'AI / Vision & Voice', trigger: 'user-action',
    description: 'GPT-4o Vision OCR for receipt photos — returns line items + total + merchant.',
    caller: 'TripSettlement → scan receipt + split expense' },
  { name: 'extract-boarding-pass', project: 'app', category: 'AI / Vision & Voice', trigger: 'user-action',
    description: 'GPT-4o Vision extraction for boarding-pass photos. Handles stamp/occlusion cases.',
    caller: 'Flights tab → "Scan boarding pass"' },
  { name: 'extract-friend-insights', project: 'app', category: 'AI / Vision & Voice', trigger: 'user-action',
    description: 'Parses a chat screenshot (WhatsApp/iMessage/IG/Telegram) into speaker-attributed lines.',
    caller: 'Friend Insights feature (companions step screenshot upload)' },
  { name: 'scan-document', project: 'app', category: 'AI / Vision & Voice', trigger: 'user-action',
    description: 'OCR + structuring for generic travel docs (visa letters, vouchers).',
    caller: 'Documents tab → "Scan document"' },
  { name: 'visual-context', project: 'app', category: 'AI / Vision & Voice', trigger: 'user-action',
    description: 'Vision context extraction — describes a user-uploaded image for trip context.',
    caller: 'Blitz / chat with image attached' },
  { name: 'transcribe-voice', project: 'app', category: 'AI / Vision & Voice', trigger: 'user-action',
    description: 'OpenAI Whisper proxy. Accepts multipart audio, returns text.',
    caller: 'Voice input on Live Dashboard + Blitz' },
  { name: 'translate', project: 'app', category: 'AI / Vision & Voice', trigger: 'user-action',
    description: 'Translate text to user\'s language with caching.',
    caller: 'Translate slide-out on Live Dashboard' },

  // ── Auth & Identity ─────────────────────────────────────────────────────
  { name: 'claim-anonymous-account', project: 'app', category: 'Auth & Identity', trigger: 'user-action',
    description: 'Converts an anonymous (signInAnonymously) user into a real email+password account, bypassing email verification.',
    caller: 'Anonymous → registered conversion banner' },
  { name: 'register-anon-signup', project: 'app', category: 'Auth & Identity', trigger: 'user-action',
    description: 'Records analytics + tenant binding immediately after signInAnonymously() completes client-side.',
    caller: 'useTenantAutoRegister effect on app boot' },
  { name: 'update-user-email', project: 'app', category: 'Auth & Identity', trigger: 'user-action',
    description: 'Platform admin tool — fixes a user\'s email after a typo so they can receive verification.',
    caller: 'Platform Tenant Detail → Users tab' },
  { name: 'invitation-preview', project: 'app', category: 'Auth & Identity', trigger: 'public',
    description: 'Public endpoint — returns invitation context for the /invite/{token} preview page.',
    caller: '/invite/{token} pre-signin page' },
  { name: 'send-invitation-email', project: 'app', category: 'Auth & Identity', trigger: 'user-action',
    description: 'Sends a branded trip-collaboration invite via Resend + fires Supabase Auth invite + in-app notification.',
    caller: 'TripCollaboratorsModal → Invite' },

  // ── Trip Guardian (flights) ─────────────────────────────────────────────
  { name: 'flight-status-poll', project: 'app', category: 'Trip Guardian (flights)', trigger: 'cron',
    description: 'Every-15-min cron. Polls FlightAware AeroAPI for every flight inside its monitor window; fans out flight-guide on disruptions.',
    caller: 'pg_cron */15 * * * *' },

  // ── Live Trip & Companion ───────────────────────────────────────────────
  { name: 'live-trip-tick', project: 'app', category: 'Live Trip & Companion', trigger: 'cron',
    description: 'Every-5-min cron. Walks all active trips, computes now/next state, buzzes web-push on activity change.',
    caller: 'pg_cron */5 * * * *' },

  // ── Social Publishing ───────────────────────────────────────────────────
  { name: 'instagram-oauth-start', project: 'app', category: 'Social Publishing', trigger: 'user-action',
    description: 'Mints an HMAC-signed state token and redirects user to Facebook OAuth consent.',
    caller: 'Settings → Connect Instagram card' },
  { name: 'instagram-oauth-callback', project: 'app', category: 'Social Publishing', trigger: 'public',
    description: 'OAuth redirect handler — exchanges code for IG Business Account access token, upserts user_social_connections.',
    caller: 'Facebook → redirect back after consent' },
  { name: 'instagram-publish-reel', project: 'app', category: 'Social Publishing', trigger: 'user-action',
    description: 'Posts a trip_reels MP4 as an Instagram Reel on the user\'s connected IG Business account.',
    caller: 'TripReelGenerateModal → "Post to Instagram"' },
  { name: 'tiktok-oauth-start', project: 'app', category: 'Social Publishing', trigger: 'user-action',
    description: 'Mints an HMAC-signed state token and redirects user to TikTok OAuth consent.',
    caller: 'Settings → Connect TikTok card' },
  { name: 'tiktok-oauth-callback', project: 'app', category: 'Social Publishing', trigger: 'public',
    description: 'OAuth redirect handler — exchanges code for TikTok access/refresh tokens, upserts user_social_connections.',
    caller: 'TikTok → redirect back after consent' },
  { name: 'tiktok-publish-reel', project: 'app', category: 'Social Publishing', trigger: 'user-action',
    description: 'Posts a trip_reels MP4 to TikTok via Inbox mode (lands in user\'s drafts; user finishes publishing in TikTok app).',
    caller: 'TripReelGenerateModal → "Post to TikTok"' },
  { name: 'youtube-oauth-start', project: 'app', category: 'Social Publishing', trigger: 'user-action',
    description: 'Mints HMAC-signed state and redirects to Google OAuth (youtube.upload + readonly scopes).',
    caller: 'Settings → Connect YouTube card' },
  { name: 'youtube-oauth-callback', project: 'app', category: 'Social Publishing', trigger: 'public',
    description: 'OAuth redirect handler — exchanges code, fetches primary channel, upserts user_social_connections.',
    caller: 'Google → redirect back after consent' },
  { name: 'youtube-publish-reel', project: 'app', category: 'Social Publishing', trigger: 'user-action',
    description: 'Uploads a trip_reels MP4 as a YouTube Short via multipart/related upload to youtube/v3/videos.',
    caller: 'TripReelGenerateModal → "Post to YouTube"' },
  { name: 'trip-reel-status', project: 'app', category: 'Social Publishing', trigger: 'user-action',
    description: 'Polled by client until Creatomate render finishes; mirrors render status into trip_reels.',
    caller: 'TripReelGenerateModal polling loop' },

  // ── Stripe / B2C billing ────────────────────────────────────────────────
  { name: 'stripe-checkout', project: 'app', category: 'Stripe / B2C billing', trigger: 'user-action',
    description: 'Creates a Stripe Checkout Session for B2C plan upgrades with revenue split.',
    caller: 'In-app plan upgrade button' },
  { name: 'stripe-cancel-subscription', project: 'app', category: 'Stripe / B2C billing', trigger: 'user-action',
    description: 'Cancels the user\'s active Stripe subscription at period end.',
    caller: 'Settings → Manage subscription → Cancel' },
  { name: 'stripe-b2c-webhook', project: 'app', category: 'Stripe / B2C billing', trigger: 'webhook',
    description: 'Handles Stripe webhook events for B2C subscriptions (created/updated/deleted/invoice).',
    caller: 'Stripe → POST to /stripe-b2c-webhook' },
  { name: 'process-referral-rewards', project: 'app', category: 'Stripe / B2C billing', trigger: 'user-action',
    description: 'Grants per-referral + milestone + referred-user rewards after a referral is recorded.',
    caller: 'useTenantAutoRegister (fire-and-forget post-signup)' },
  { name: 'reset-user-free-plan', project: 'app', category: 'Stripe / B2C billing', trigger: 'user-action',
    description: 'Super-admin override to clear a user\'s free_plan_used_at, re-enabling one-shot free plan.',
    caller: 'Platform Tenant Detail → Users tab' },

  // ── Email & Push ────────────────────────────────────────────────────────
  { name: 'send-push-to-user', project: 'app', category: 'Email & Push', trigger: 'internal',
    description: 'Reusable web-push delivery endpoint. Service-role bearer gated (security fix C1).',
    caller: 'flight-guide, flight-status-poll, live-trip-tick, smart-companion' },
  { name: 'send-notifications', project: 'app', category: 'Email & Push', trigger: 'internal',
    description: 'Centralised notification dispatch (in-app + email + push, channel-aware).',
    caller: 'Various server-side events (trip changes, invites)' },
  { name: 'send-split-email', project: 'app', category: 'Email & Push', trigger: 'db-trigger',
    description: 'Sends expense-split lifecycle emails (created, accepted, disputed, etc.).',
    caller: 'Postgres trigger on splits table via pg_net' },
  { name: 'campaign-unsubscribe', project: 'app', category: 'Email & Push', trigger: 'public',
    description: 'Public HMAC-signed link to opt out of campaign emails; updates notification_preferences.',
    caller: 'Unsubscribe link in campaign emails' },
  { name: 'notify-chat-message', project: 'app', category: 'Email & Push', trigger: 'db-trigger',
    description: 'Web-push delivery for chat events (new message, reaction, poll vote).',
    caller: 'Postgres triggers on trip_chat tables via pg_net' },
  { name: 'notify-books-closing', project: 'app', category: 'Email & Push', trigger: 'cron',
    description: 'Daily cron — emails trip owners 24h before books_close_at to log final expenses.',
    caller: 'pg_cron daily (~02:00 UTC)' },
  { name: 'dispatch-buddy-moderation-event', project: 'app', category: 'Email & Push', trigger: 'db-trigger',
    description: 'Fan-out for Buddy Trips auto-block + restore events (notifications + admin alerts).',
    caller: 'post_buddy_message + admin_force_unblock_buddy_user via pg_net' },
  { name: 'messaging-inbound', project: 'app', category: 'Email & Push', trigger: 'webhook',
    description: 'Twilio WhatsApp inbound webhook — opt-in / STOP / HELP, the numbered MENU trip Q&A, and the multi-step "plan a trip" conversational build.',
    caller: 'Twilio WhatsApp → auth.discoverygem.com/functions/v1/messaging-inbound' },
  { name: 'send-whatsapp-to-user', project: 'app', category: 'Email & Push', trigger: 'internal',
    description: 'WhatsApp delivery primitive (Twilio) — canned nudges, custom body, or approved template ContentSid. Service-role gated.',
    caller: 'notify-me-whatsapp, messaging-inbound (the "trip ready" link), proactive nudge engine' },
  { name: 'notify-me-whatsapp', project: 'app', category: 'Email & Push', trigger: 'user-action',
    description: 'Authenticated "send a WhatsApp to myself" — consent-gated, resolves the tenant brand, delegates to send-whatsapp-to-user. Powers the opt-in welcome.',
    caller: 'WhatsApp opt-in welcome (whatsappWelcome.ts), in-app test send' },

  // ── Cron / Cleanup ──────────────────────────────────────────────────────
  { name: 'auto-accept-splits', project: 'app', category: 'Cron / Cleanup', trigger: 'cron',
    description: 'Daily cron flipping expired pending_review splits to accepted after the 7-day window.',
    caller: 'pg_cron daily (~02:00 UTC)' },
  { name: 'cleanup-anonymous-users', project: 'app', category: 'Cron / Cleanup', trigger: 'cron',
    description: 'Deletes anonymous guest accounts inactive beyond each tenant\'s retention window.',
    caller: 'Triggered by Platform trigger-anon-cleanup orchestrator (daily)' },
  { name: 'cleanup-expired-artifacts', project: 'app', category: 'Cron / Cleanup', trigger: 'cron',
    description: 'Daily cron enforcing data_retention_policies — purges boarding passes, scanned docs, disruption events.',
    caller: 'pg_cron daily (~03:00 UTC)' },
  { name: 'archive-expired-trips', project: 'app', category: 'Cron / Cleanup', trigger: 'cron',
    description: 'Daily cron — archives saved trips whose planned window is already in the past (timezone-safe, UTC-1-day), across ALL tenants. Active-trip expiry stays a per-user RPC.',
    caller: 'pg_cron daily (~04:00 UTC)' },

  // ── Cross-project bridge (Platform → App) ───────────────────────────────
  { name: 'provision-tenant', project: 'app', category: 'Cross-project bridge', trigger: 'internal',
    description: 'Seeds a new tenant\'s App DB (B2C plans, features, plan-feature mappings).',
    caller: 'Platform Provision Wizard after creating tenant on Platform DB' },
  { name: 'delete-tenant', project: 'app', category: 'Cross-project bridge', trigger: 'internal',
    description: 'Wipes all App DB data for a tenant via cleanup_entire_tenant RPC (security definer).',
    caller: 'Platform Tenant Detail → Danger Zone → Delete' },
  { name: 'fetch-audit-log', project: 'app', category: 'Cross-project bridge', trigger: 'internal',
    description: 'Returns paginated admin_audit_log entries to Platform for a tenant.',
    caller: 'Platform Tenant Detail → Audit Log tab' },
  { name: 'tenant-metrics', project: 'app', category: 'Cross-project bridge', trigger: 'internal',
    description: 'Real-time usage metrics for one tenant (user count, trip count, storage, AI calls).',
    caller: 'Platform sync-usage-metrics pipeline' },
  { name: 'tenant-dashboard-extras', project: 'app', category: 'Cross-project bridge', trigger: 'internal',
    description: 'Extras for Platform tenant dashboard not derivable from usage_metrics alone.',
    caller: 'Platform fetch-dashboard-extras' },
  { name: 'tenant-trips', project: 'app', category: 'Cross-project bridge', trigger: 'internal',
    description: 'Returns trip list + owner info + collaborator counts for a tenant.',
    caller: 'Platform Tenant Detail → Trips tab' },
  { name: 'tenant-users', project: 'app', category: 'Cross-project bridge', trigger: 'internal',
    description: 'Returns user list + current plan + subscription status for a tenant.',
    caller: 'Platform Tenant Detail → Users tab' },
  { name: 'tenant-referrals', project: 'app', category: 'Cross-project bridge', trigger: 'internal',
    description: 'Referral codes + records + summary stats for a tenant.',
    caller: 'Platform Tenant Detail → Referrals tab' },
  { name: 'list-tenant-feedback', project: 'app', category: 'Cross-project bridge', trigger: 'internal',
    description: 'Reader for tenant_feedback rows surfaced to Platform feedback page.',
    caller: 'Platform super-admin Feedback page + Tenant Detail Feedback tab' },
  { name: 'user-subscription-history', project: 'app', category: 'Cross-project bridge', trigger: 'internal',
    description: 'Returns user_subscription_history timeline for one end-user.',
    caller: 'Platform Tenant Detail → User detail' },
  { name: 'b2c-pricing-benchmark', project: 'app', category: 'Cross-project bridge', trigger: 'internal',
    description: 'Anonymous aggregate over other tenants\' B2C pricing (privacy-preserving, ≥3 per bucket).',
    caller: 'Platform Pricing Assistant (B2C variant)' },
  { name: 'fetch-tenant-pricing', project: 'app', category: 'Cross-project bridge', trigger: 'internal',
    description: 'Returns B2C pricing plans + revenue share for a specific tenant.',
    caller: 'Platform Tenant Detail → Pricing tab' },
  { name: 'update-tenant-pricing', project: 'app', category: 'Cross-project bridge', trigger: 'internal',
    description: 'Writes B2C pricing changes (prices, revenue share) on behalf of Platform.',
    caller: 'Platform Tenant Detail → Pricing edit' },
  { name: 'manage-b2c-plans', project: 'app', category: 'Cross-project bridge', trigger: 'internal',
    description: 'Full CRUD for tenant B2C pricing plans + Stripe Product/Price automation + ceiling sync.',
    caller: 'Platform Tenant Admin B2C Plans page' },
  { name: 'manage-plan-features', project: 'app', category: 'Cross-project bridge', trigger: 'internal',
    description: 'Read/write B2C tenant_plan_features. Enforces super-admin ceilings + tenant admin overrides.',
    caller: 'Platform Feature Gating + Tenant Admin Plans pages' },
  { name: 'manage-user-overrides', project: 'app', category: 'Cross-project bridge', trigger: 'internal',
    description: 'Read/write B2C user_feature_overrides — per-user feature toggles above plan defaults.',
    caller: 'Platform User detail → Feature overrides' },
  { name: 'manage-tenant-user', project: 'app', category: 'Cross-project bridge', trigger: 'internal',
    description: 'Manages users within a tenant (invite, promote/demote, suspend, delete).',
    caller: 'Platform Tenant Detail → Users actions' },
  { name: 'admin-buddy-analytics', project: 'app', category: 'Cross-project bridge', trigger: 'internal',
    description: 'Buddy Trips analytics + moderation tools (block/unblock, audit trail).',
    caller: 'Platform Tenant Detail → Buddy tab + super-admin Buddy page' },
  { name: 'admin-retention-policies', project: 'app', category: 'Cross-project bridge', trigger: 'internal',
    description: 'CRUD for App DB data_retention_policies. Platform-side defaults + per-tenant overrides.',
    caller: 'Platform Retention Defaults + Tenant Detail Retention' },
  { name: 'admin-impersonate-user', project: 'app', category: 'Cross-project bridge', trigger: 'internal',
    description: 'Mints a hashed_token via auth.admin.generateLink for super-admin "Sign in as user". Validates target is in target tenant. Audits to admin_audit_log.',
    caller: 'Platform start-impersonation (x-provision-secret gated)' },

  // ── Lookup / Proxy ──────────────────────────────────────────────────────
  { name: 'geocoding', project: 'app', category: 'Lookup / Proxy', trigger: 'user-action',
    description: 'City→coords + city image waterfall (Google + Unsplash fallback) with caching.',
    caller: 'Trip Builder city pills + ResultsScreen vibe-photo hydration' },
  { name: 'city-autocomplete', project: 'app', category: 'Lookup / Proxy', trigger: 'user-action',
    description: 'Google Places city search narrowed to LOCALITY (no districts/airports).',
    caller: 'TripDetailsModal "Flying from" input' },
  { name: 'hotel-autocomplete', project: 'app', category: 'Lookup / Proxy', trigger: 'user-action',
    description: 'Google Places hotel search with debounce + popular cache + per-plan daily quota gate.',
    caller: 'AiTripBuilder hotel step + Near Hotel popup' },
  { name: 'fx-rate', project: 'app', category: 'Lookup / Proxy', trigger: 'user-action',
    description: 'Server-side FX rate proxy (avoids browser-side ad-blocker/firewall issues).',
    caller: 'TripSettlement currency conversion' },
  { name: 'weather', project: 'app', category: 'Lookup / Proxy', trigger: 'user-action',
    description: 'Weather forecast proxy + per-destination caching.',
    caller: 'Live Dashboard weather strip + Itinerary day cards' },
  { name: 'routes', project: 'app', category: 'Lookup / Proxy', trigger: 'user-action',
    description: 'Google Directions proxy with (origin, destination, mode) caching for transit + daily moves.',
    caller: 'Transit tab + Daily Moves timeline' },
  { name: 'unsplash', project: 'app', category: 'Lookup / Proxy', trigger: 'user-action',
    description: 'Unsplash image search proxy with caching.',
    caller: 'Vibe-photo / activity-image fallback' },

  // ── Other (App) ─────────────────────────────────────────────────────────
  { name: 'create-daily-room', project: 'app', category: 'Other', trigger: 'user-action',
    description: 'Creates a Daily.co audio-only room for a live build session; returns URL + meeting tokens.',
    caller: 'Live Build session host/join flow' },
  { name: 'zoom-meeting', project: 'app', category: 'Other', trigger: 'user-action',
    description: 'Creates a Zoom meeting via Server-to-Server OAuth; emails invites to trip collaborators.',
    caller: 'TripCollaboratorsModal → Schedule Zoom' },
];

// ─────────────────────────────────────────────────────────────────────────────
// PLATFORM project — apps/unravel-platform/supabase/functions/
// ─────────────────────────────────────────────────────────────────────────────

const PLATFORM: EdgeFnEntry[] = [
  // ── AI / Assistance (Platform) ──────────────────────────────────────────
  { name: 'branding-assistant', project: 'platform', category: 'AI / Assistance', trigger: 'user-action',
    description: 'Conversational AI branding assistant (Claude). Drafts brand voice, taglines, palettes.',
    caller: 'Tenant Admin → Branding page' },
  { name: 'pricing-assistant', project: 'platform', category: 'AI / Assistance', trigger: 'user-action',
    description: 'Multi-turn Claude chat for B2B plan pricing — competitor comparison, anchoring guidance.',
    caller: 'Plan Builder side panel' },
  { name: 'pricing-assistant-b2c', project: 'platform', category: 'AI / Assistance', trigger: 'user-action',
    description: 'B2C variant of pricing assistant. Pulls anonymous benchmarks from App DB.',
    caller: 'Tenant Admin → B2C Plans page' },
  { name: 'ai-bulletin-from-ig', project: 'platform', category: 'AI / Assistance', trigger: 'user-action',
    description: 'Turns a pasted Instagram caption into a structured bulletin draft.',
    caller: 'Tenant Admin → Bulletins → "Import from IG"' },
  { name: 'ai-bulletin-from-tiktok', project: 'platform', category: 'AI / Assistance', trigger: 'user-action',
    description: 'Same as ai-bulletin-from-ig but tuned for TikTok caption conventions.',
    caller: 'Tenant Admin → Bulletins → "Import from TikTok"' },
  { name: 'ask-me-chat', project: 'platform', category: 'AI / Assistance', trigger: 'user-action',
    description: 'Contextual AI help assistant — answers tenant-admin questions about Platform features.',
    caller: 'AskMeWidget across all super-admin + tenant-admin pages' },
  { name: 'youtube-import', project: 'platform', category: 'AI / Assistance', trigger: 'user-action',
    description: 'AI bulk-import of YouTube channel videos into the content feed (admin-only).',
    caller: 'Platform admin → Content Feed → "Import channel"' },

  // ── Stripe / B2B billing ────────────────────────────────────────────────
  { name: 'b2b-checkout', project: 'platform', category: 'Stripe / B2B billing', trigger: 'user-action',
    description: 'Creates a Stripe Checkout Session for tenant B2B plan upgrades.',
    caller: 'Tenant Admin → Billing → Upgrade' },
  { name: 'manage-b2b-plans', project: 'platform', category: 'Stripe / B2B billing', trigger: 'user-action',
    description: 'Auto-creates Stripe Products + recurring Prices when super-admin saves a plan in Plan Builder.',
    caller: 'Super-admin Plan Builder save' },
  { name: 'stripe-billing-portal', project: 'platform', category: 'Stripe / B2B billing', trigger: 'user-action',
    description: 'Creates a Stripe Billing Portal session URL for managing payment methods.',
    caller: 'Tenant Admin → Billing → Manage payment' },
  { name: 'stripe-payment-method', project: 'platform', category: 'Stripe / B2B billing', trigger: 'user-action',
    description: 'Fetches default payment method (brand, last4, expiry) for a tenant\'s Stripe customer.',
    caller: 'Tenant Admin Billing page card' },
  { name: 'fetch-invoices', project: 'platform', category: 'Stripe / B2B billing', trigger: 'user-action',
    description: 'Returns real Stripe invoices for a tenant.',
    caller: 'Tenant Admin → Billing → Invoices' },
  { name: 'update-seats', project: 'platform', category: 'Stripe / B2B billing', trigger: 'user-action',
    description: 'Updates seat count (quantity) on an existing Stripe subscription with prorated billing.',
    caller: 'Tenant Admin → Users → Seats slider' },
  { name: 'update-subscription', project: 'platform', category: 'Stripe / B2B billing', trigger: 'user-action',
    description: 'Downgrade flow — cancel-at-period-end or switch tier with proration.',
    caller: 'Tenant Admin → Billing → Change plan' },
  { name: 'stripe-connect-onboarding', project: 'platform', category: 'Stripe / B2B billing', trigger: 'user-action',
    description: 'Creates a Stripe Connect Express account for a tenant to receive B2C payouts.',
    caller: 'Tenant Admin → Payouts onboarding' },
  { name: 'stripe-connect-status', project: 'platform', category: 'Stripe / B2B billing', trigger: 'user-action',
    description: 'Checks Stripe Connect onboarding capability status (charges_enabled, payouts_enabled).',
    caller: 'Tenant Admin → Payouts status badge' },
  { name: 'stripe-webhook', project: 'platform', category: 'Stripe / B2B billing', trigger: 'webhook',
    description: 'Handles Stripe webhook events for B2B subscription lifecycle (created/updated/deleted/invoice).',
    caller: 'Stripe → POST to /stripe-webhook' },
  { name: 'validate-referral-code', project: 'platform', category: 'Stripe / B2B billing', trigger: 'public',
    description: 'Public endpoint — validates a B2B referral code during tenant signup/checkout.',
    caller: 'Public signup/checkout flow' },

  // ── Cross-project bridge (Platform-side) ────────────────────────────────
  { name: 'fetch-dashboard-extras', project: 'platform', category: 'Cross-project bridge', trigger: 'user-action',
    description: 'Proxy to App DB tenant-dashboard-extras. User-JWT in, service-role-cross-project out.',
    caller: 'Super-admin Tenant Detail dashboard' },
  { name: 'sync-usage-metrics', project: 'platform', category: 'Cross-project bridge', trigger: 'cron',
    description: 'Daily cron — pulls per-tenant metrics from App DB via tenant-metrics, writes Platform usage_metrics snapshots.',
    caller: 'pg_cron daily (~01:00 UTC)' },
  { name: 'sync-tenant-admin', project: 'platform', category: 'Cross-project bridge', trigger: 'user-action',
    description: 'Syncs admin-role changes between App DB (tenant_users) and Platform DB (tenant_admins).',
    caller: 'After manage-tenant-user role change' },
  { name: 'trigger-anon-cleanup', project: 'platform', category: 'Cross-project bridge', trigger: 'cron',
    description: 'Daily orchestrator — resolves anon retention windows per tenant, calls App cleanup-anonymous-users.',
    caller: 'pg_cron daily (~04:00 UTC)' },

  // ── Tenant & User admin ─────────────────────────────────────────────────
  { name: 'invite-tenant-admin', project: 'platform', category: 'Tenant & User admin', trigger: 'user-action',
    description: 'Creates auth user, generates invite link, inserts tenant_admins row, sends branded invite email.',
    caller: 'Super-admin Tenant Detail → Invite Admin + Provision Wizard' },
  { name: 'tenant-config', project: 'platform', category: 'Tenant & User admin', trigger: 'public',
    description: 'Public boot-time API — given a domain, returns the tenant\'s branding + flags. Called on App startup.',
    caller: 'App boot (every user, every page load)' },

  // ── Domain / SSL ────────────────────────────────────────────────────────
  { name: 'provision-subdomain', project: 'platform', category: 'Tenant & User admin', trigger: 'user-action',
    description: 'Creates CNAME in Cloudflare DNS + Netlify domain alias for new tenant slug.',
    caller: 'Provision Wizard step 3' },
  { name: 'cleanup-subdomain', project: 'platform', category: 'Tenant & User admin', trigger: 'user-action',
    description: 'Reverse of provision-subdomain — removes DNS + domain alias on tenant delete.',
    caller: 'Tenant Detail → Danger Zone → Delete' },
  { name: 'verify-domain', project: 'platform', category: 'Tenant & User admin', trigger: 'user-action',
    description: 'Checks DNS TXT verification token; on success registers domain with Cloudflare for SaaS.',
    caller: 'Tenant Admin → Custom domain → Verify' },
  { name: 'check-domain-ssl', project: 'platform', category: 'Tenant & User admin', trigger: 'user-action',
    description: 'Polls Cloudflare Custom Hostname SSL status; updates ssl_status in DB.',
    caller: 'Tenant Admin Custom domain page polling' },
  { name: 'remove-domain', project: 'platform', category: 'Tenant & User admin', trigger: 'user-action',
    description: 'Removes a custom domain — deletes Cloudflare Custom Hostname + DB row.',
    caller: 'Tenant Admin Custom domain page → Remove' },

  // ── Email & Push (Platform) ─────────────────────────────────────────────
  { name: 'send-campaign', project: 'platform', category: 'Email & Push', trigger: 'user-action',
    description: 'Sends campaign emails to B2C users for a tenant (segmented).',
    caller: 'Tenant Admin → Campaigns → Send' },
  { name: 'send-notification', project: 'platform', category: 'Email & Push', trigger: 'internal',
    description: 'Central notification delivery — checks platform + tenant toggles, sends branded email via Resend.',
    caller: 'Various Platform events (tenant invite, plan change, ticket reply)' },
  { name: 'send-platform-announcement', project: 'platform', category: 'Email & Push', trigger: 'user-action',
    description: 'Sends branded announcement emails to tenant admins (target = all/segment/single).',
    caller: 'Super-admin → Platform Announcements page' },

  // ── Public / Anonymous ──────────────────────────────────────────────────
  { name: 'business-lead', project: 'platform', category: 'Public / Anonymous', trigger: 'public',
    description: 'Captures business leads from register.discoverygem.com.',
    caller: 'Public register page form' },
  { name: 'contact-inquiry', project: 'platform', category: 'Public / Anonymous', trigger: 'public',
    description: 'Captures contact form submissions from public /contact page.',
    caller: 'Public /contact page' },
  { name: 'booking-inquiry', project: 'platform', category: 'Public / Anonymous', trigger: 'public',
    description: 'Captures detailed booking inquiries from B2C users viewing a bulletin.',
    caller: 'B2C app bulletin → Inquire' },
  { name: 'bulletin-interest', project: 'platform', category: 'Public / Anonymous', trigger: 'public',
    description: 'Lightweight "I\'m interested" signal for a bulletin (no full inquiry payload).',
    caller: 'B2C app bulletin → Heart / Save' },
  { name: 'affiliate-click', project: 'platform', category: 'Public / Anonymous', trigger: 'public',
    description: 'Tracks affiliate link clicks from B2C apps, returns the destination URL.',
    caller: 'Affiliate redirect link on tenant B2C app' },
  { name: 'content-feed', project: 'platform', category: 'Public / Anonymous', trigger: 'public',
    description: 'Public read-only content feed for B2C splash pages (blog, YouTube, IG).',
    caller: 'B2C splash page sections' },
  // vision-access: deployed but orphaned — the /vision public co-founder
  // pitch page was removed. The function and vision_access_tokens table
  // remain deployed so the migration history stays clean; if anyone needs
  // them back, restore the page and re-add this catalog entry.

  // ── Infra & Ops ─────────────────────────────────────────────────────────
  { name: 'infra-list', project: 'platform', category: 'Infra & Ops', trigger: 'user-action',
    description: 'Read-only inventory for /admin/infrastructure: scheduled functions, vault secrets, edge secrets, job runs.',
    caller: 'Super-admin Infrastructure page' },
  { name: 'infra-mutate', project: 'platform', category: 'Infra & Ops', trigger: 'user-action',
    description: 'Companion to infra-list — toggle cron, vault CRUD, run-cron-now. Audits every mutation.',
    caller: 'Super-admin Infrastructure page actions' },
  { name: 'manage-app-secrets', project: 'platform', category: 'Infra & Ops', trigger: 'user-action',
    description: 'Wraps Supabase Management API to view + unset whitelisted override secrets on the App project.',
    caller: 'Super-admin AI Settings → Secret rows' },
  { name: 'start-impersonation', project: 'platform', category: 'Infra & Ops', trigger: 'user-action',
    description: 'Initiates super-admin "Sign in as user". Verifies platform_admin, resolves tenant host, cross-calls App admin-impersonate-user, builds direct URL with the hashed_token. Audits to platform_audit_log.',
    caller: 'Super-admin Tenant Detail → Users tab → "Sign in as" button' },
];

// ─────────────────────────────────────────────────────────────────────────────

export const EDGE_FUNCTIONS: EdgeFnEntry[] = [...APP, ...PLATFORM];

/** Distinct categories present in the catalog — used to build filter
 *  dropdowns dynamically so we don't have to manually keep the page
 *  in sync if a new category is introduced. */
export const EDGE_FN_CATEGORIES: EdgeFnCategory[] = Array.from(
  new Set(EDGE_FUNCTIONS.map(fn => fn.category)),
).sort() as EdgeFnCategory[];

/** Trigger types present in the catalog. */
export const EDGE_FN_TRIGGERS: EdgeFnTrigger[] = Array.from(
  new Set(EDGE_FUNCTIONS.map(fn => fn.trigger)),
).sort() as EdgeFnTrigger[];
